import { ref, onMounted } from 'vue';
import { debounce } from '@/utils/api';
import { useRouter } from '@/utils/vue-api';

/**
 * 滚动联动功能
 * 包含滚动观察器管理和生命周期管理
 */
export const useScrollSync = centralDispatcher => {
	let wrapObserver = null;
	let scrollObserver = null;

	const skipScrollObserver = ref(false);
	const stopScrollObserver = () => {
		skipScrollObserver.value = true;
	};

	const destroyWrapObserver = () => {
		if (wrapObserver) {
			wrapObserver.disconnect();
			wrapObserver = null;
		}
	};

	const createWrapObserver = updateFrameCallback => {
		wrapObserver = new MutationObserver(mutations => {
			mutations.forEach(() => {
				// 滚动条会影响页面宽度计算 需要多次校验
				updateFrameCallback();
			});
		});
		wrapObserver.observe(document.body, {
			childList: true,
			subtree: true
		});
	};

	const destroyScrollObserver = () => {
		if (scrollObserver) {
			scrollObserver.disconnect();
			scrollObserver = null;
		}
	};

	// 创建滚动观察器，监听目标菜单的可见性
	const createScrollObserver = updateActiveMenuIdWithSwiperCallback => {
		// 通过派发器获取数据，避免参数传递
		const { currentMenus } = centralDispatcher.getState();
		const { nodesMap } = centralDispatcher.getConfig();

		if (scrollObserver) {
			scrollObserver.disconnect();
			scrollObserver = null;
		}

		const menuItems = document.querySelectorAll(nodesMap.menuItem);
		if (!menuItems.length) return;

		const elementToIndexMap = new WeakMap(); // 创建DOM元素到索引的映射
		const activeItems = new Set(); // 存储当前在视口内的元素

		const observerOptions = {
			root: null, // 默认是视口
			rootMargin: '0px',
			threshold: 0.5 // 元素至少有50%进入视口时触发回调
		};

		const observerCallback = entries => {
			if (skipScrollObserver.value) return;

			entries.forEach(entry => {
				if (entry.isIntersecting) {
					activeItems.add(entry.target);
				} else {
					activeItems.delete(entry.target);
				}
			});

			// 仅为最靠前的激活元素处理菜单选中
			if (activeItems.size > 0) {
				// 找到索引最小的激活元素
				let minIndex = Infinity;
				let mostTopItem = null;

				for (const item of activeItems) {
					const index = elementToIndexMap.get(item);
					if (index < minIndex) {
						minIndex = index;
						mostTopItem = item;
					}
				}

				if (mostTopItem && minIndex < currentMenus.length) {
					const findTopMenu = currentMenus[minIndex];
					if (findTopMenu) {
						updateActiveMenuIdWithSwiperCallback(findTopMenu.id);
					}
				}
			}
		};

		scrollObserver = new IntersectionObserver(observerCallback, observerOptions);
		menuItems.forEach((item, index) => {
			elementToIndexMap.set(item, index); // 建立DOM元素与菜单索引的映射关系
			scrollObserver.observe(item);
		});
	};

	// 监听滚动停止事件
	const onScrollStop = () => {
		if (skipScrollObserver.value) {
			skipScrollObserver.value = false;
		}
	};

	// 生命周期管理
	const debouncedOnScrollStop = debounce(onScrollStop, 200);
	const router = useRouter();
	onMounted(() => {
		// 路由变化时销毁观察器
		router.afterEach(() => {
			destroyWrapObserver();
		});
	});

	// 挂载时的初始化方法
	const mount = updateFrameCallback => {
		// 监听滚动停止事件
		window.addEventListener('scroll', debouncedOnScrollStop);

		// 创建观察器并触发初始更新
		createWrapObserver(updateFrameCallback);
		updateFrameCallback();
	};

	// 卸载时的清理方法
	const unmount = () => {
		// 移除滚动监听
		window.removeEventListener('scroll', debouncedOnScrollStop);

		// 销毁观察器
		destroyWrapObserver();
		destroyScrollObserver();
	};

	return {
		skipScrollObserver,
		stopScrollObserver,
		destroyWrapObserver,
		createWrapObserver,
		destroyScrollObserver,
		createScrollObserver,
		onScrollStop,
		mount,
		unmount
	};
};
